import { Box, Typography, Grid, TextField, Alert, Radio, FormControlLabel } from "@mui/material";
import moment, { Moment } from "moment";
import { useState, useEffect, useCallback, useRef } from "react";
import { formatarValorMonetario } from "../../../../../../../../../helpers/masks";
import BasicDatePicker from "../../../../../../../../../components/BasicDatePicker";

export type TParcela = {
    numero_parcela: number,
    valor_parcela: string,
    data_vencimento: Moment,
};

type TObjParcelas = {
    quantidade: string;
    intervalo: string;
    data_inicial: Moment | null;
    dias_corridos_primeira_parcela: string;
};

type TTipoPrimeiraParcela = 'personalizada' | 'dias_corridos';

export interface PagamentoParceladoProps {
    valorTotal: number;
    parcelas: TParcela[];
    onChange: (parcelas: TParcela[]) => void;
    onFormaPrimeiraParcelaChange?: (forma: TTipoPrimeiraParcela, diasCorridos?: string) => void;
    minParcelas?: number;
    maxParcelas?: number;
    erro?: string;
    onValorPrimeiraParcelaChange?: (valor: number | null) => void
    valorPrimeiraParcelaInicial?: number | null;
    habilitarSelecaoPrimeiraParcela?: boolean;
}

function calcularValoresParcelas(valorTotal: number, quantidade: number, valorPrimeiraParcela?: number) {
    if (valorPrimeiraParcela !== undefined && quantidade > 1) {
        const valorRestante = valorTotal - valorPrimeiraParcela;
        const quantidadeParcelasRestantes = quantidade - 1;

        if (quantidadeParcelasRestantes > 0) {
            const valorParcelaRestante = Math.floor((valorRestante / quantidadeParcelasRestantes) * 100) / 100;
            const valorUltimaParcelaRestante = valorParcelaRestante + (valorRestante - (valorParcelaRestante * quantidadeParcelasRestantes));

            return {
                valorPrimeiraParcelaFormatado: valorPrimeiraParcela.toFixed(2).replace('.', ','),
                valorParcelaFormatado: valorParcelaRestante.toFixed(2).replace('.', ','),
                valorUltimaParcelaFormatado: valorUltimaParcelaRestante.toFixed(2).replace('.', ',')
            };
        }
    }

    const valorParcela = Math.floor((valorTotal / quantidade) * 100) / 100;
    const valorUltimaParcela = valorParcela + (valorTotal - (valorParcela * quantidade));
    const valorParcelaFormatado = valorParcela.toFixed(2).replace('.', ',');
    const valorUltimaParcelaFormatado = valorUltimaParcela.toFixed(2).replace('.', ',');
    return {
        valorPrimeiraParcelaFormatado: valorParcelaFormatado,
        valorParcelaFormatado,
        valorUltimaParcelaFormatado
    }
}


function calcularDataInicial(tipo: TTipoPrimeiraParcela, dataFixa?: Moment, quantidadeDias?: number): Moment {
    const hoje = moment();

    switch (tipo) {
        case 'dias_corridos':
            return hoje.clone().add(quantidadeDias || 0, 'days');
        case 'personalizada':
        default:
            return dataFixa || hoje;
    }
}

function PagamentoParcelado({
    valorTotal,
    parcelas,
    onChange,
    onFormaPrimeiraParcelaChange,
    minParcelas = 1,
    maxParcelas = 36,
    erro,
    habilitarSelecaoPrimeiraParcela = false,
    onValorPrimeiraParcelaChange,
    valorPrimeiraParcelaInicial
}: PagamentoParceladoProps) {

    const [parcelado, setParcelado] = useState(Array.isArray(parcelas) && parcelas.length > 1 ? true : false);
    const [gerarParcelas, setGerarParcelas] = useState<TObjParcelas>({
        quantidade: Array.isArray(parcelas) && parcelas.length > 1 ? String(parcelas.length) : '1',
        intervalo: '30',
        data_inicial: (Array.isArray(parcelas) && parcelas[0]?.data_vencimento) || moment(),
        dias_corridos_primeira_parcela: '30',
    });

    const [parcelasLocal, setParcelasLocal] = useState<TParcela[]>([]);
    const [tipoPrimeiraParcela, setTipoPrimeiraParcela] = useState<TTipoPrimeiraParcela>('personalizada');
    const [valorPrimeiraParcelaCustomizado, setValorPrimeiraParcelaCustomizado] = useState<number | null>(null);
    const [valorPrimeiraParcelaInput, setValorPrimeiraParcelaInput] = useState<string>('');
    const [erroValorPrimeiraParcela, setErroValorPrimeiraParcela] = useState<string>('');

    const [localQuantidade, setLocalQuantidade] = useState(gerarParcelas.quantidade);
    const [localIntervalo, setLocalIntervalo] = useState(gerarParcelas.intervalo);
    const [localDiasCorridos, setLocalDiasCorridos] = useState(gerarParcelas.dias_corridos_primeira_parcela);

    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isInitialMount = useRef(true);

    const debouncedUpdateString = useCallback((key: 'quantidade' | 'intervalo' | 'dias_corridos_primeira_parcela', value: string) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            setGerarParcelas(prev => ({ ...prev, [key]: value }));
        }, 300);
    }, []);

    const debouncedUpdateMoment = useCallback((key: 'data_inicial', value: Moment | null) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            setGerarParcelas(prev => ({ ...prev, [key]: value }));
        }, 300);
    }, []);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const valorInicialSincronizado = useRef(false);

    useEffect(() => {
        if (valorPrimeiraParcelaInicial !== undefined && valorPrimeiraParcelaInicial !== null) {
            const valorFormatado = valorPrimeiraParcelaInicial.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

            if (valorPrimeiraParcelaInput !== valorFormatado) {
                setValorPrimeiraParcelaCustomizado(valorPrimeiraParcelaInicial);
                setValorPrimeiraParcelaInput(valorFormatado);

                if (!valorInicialSincronizado.current) {
                    valorInicialSincronizado.current = true;
                    setParcelado(true);
                }
            }
        }
    }, [valorPrimeiraParcelaInicial, valorPrimeiraParcelaInput]);

    useEffect(() => {
        if (onFormaPrimeiraParcelaChange) {
            onFormaPrimeiraParcelaChange(tipoPrimeiraParcela, gerarParcelas.dias_corridos_primeira_parcela);
        }
    }, [tipoPrimeiraParcela, gerarParcelas.dias_corridos_primeira_parcela, onFormaPrimeiraParcelaChange]);

    useEffect(() => {
        if (valorPrimeiraParcelaCustomizado && valorPrimeiraParcelaCustomizado > valorTotal) {
            setValorPrimeiraParcelaCustomizado(null);
            setValorPrimeiraParcelaInput('');
            setErroValorPrimeiraParcela('');
        }
    }, [valorTotal, valorPrimeiraParcelaCustomizado]);

    useEffect(() => {
        if (Array.isArray(parcelas) && parcelas.length > 0 && isInitialMount.current) {
            setParcelasLocal(parcelas);
            isInitialMount.current = false;
        } else if (parcelasLocal.length === 0 && !parcelado) {
            const parcelaUnica = [{
                numero_parcela: 1,
                valor_parcela: valorTotal.toFixed(2).replace('.', ','),
                data_vencimento: moment(),
            }];
            setParcelasLocal(parcelaUnica);
            onChange(parcelaUnica);
        }
    }, [parcelas, valorTotal, parcelasLocal.length, parcelado]);

    useEffect(() => {
        if (habilitarSelecaoPrimeiraParcela) {
            if (tipoPrimeiraParcela === 'personalizada') {
                setGerarParcelas(prev => ({ ...prev, data_inicial: moment() }));
            } else {
                const quantidadeDias = Number(gerarParcelas.dias_corridos_primeira_parcela);
                const novaDataInicial = calcularDataInicial(tipoPrimeiraParcela, undefined, quantidadeDias);
                setGerarParcelas(prev => ({ ...prev, data_inicial: novaDataInicial }));
            }
        }
    }, [tipoPrimeiraParcela, habilitarSelecaoPrimeiraParcela, gerarParcelas.dias_corridos_primeira_parcela]);

    useEffect(() => {
        if (parcelado && habilitarSelecaoPrimeiraParcela && tipoPrimeiraParcela === 'personalizada' && gerarParcelas.data_inicial && Number(gerarParcelas.quantidade) > 1) {
            const quantidade = Number(gerarParcelas.quantidade);
            const intervalo = Number(gerarParcelas.intervalo);

            const valores = calcularValoresParcelas(valorTotal, quantidade, valorPrimeiraParcelaCustomizado || undefined);

            const novasParcelas: TParcela[] = Array.from({ length: quantidade }, (_, index) => {
                const numero_parcela = index + 1;
                const data_vencimento = moment(gerarParcelas.data_inicial).add(intervalo * index, 'days');

                let valor_parcela: string;
                if (numero_parcela === 1) {
                    valor_parcela = valores.valorPrimeiraParcelaFormatado;
                } else if (numero_parcela === quantidade) {
                    valor_parcela = valores.valorUltimaParcelaFormatado;
                } else {
                    valor_parcela = valores.valorParcelaFormatado;
                }

                return {
                    numero_parcela,
                    data_vencimento,
                    valor_parcela
                };
            });

            setParcelasLocal(novasParcelas);
            onChange(novasParcelas);
        }
    }, [tipoPrimeiraParcela, gerarParcelas.data_inicial, gerarParcelas.intervalo, gerarParcelas.quantidade, parcelado, valorTotal, habilitarSelecaoPrimeiraParcela, valorPrimeiraParcelaCustomizado]);

    useEffect(() => {
        if (!parcelado && parcelasLocal.length === 1 && gerarParcelas.data_inicial) {
            const parcelaUnica = [{
                numero_parcela: 1,
                valor_parcela: valorTotal.toFixed(2).replace('.', ','),
                data_vencimento: gerarParcelas.data_inicial,
            }];
            setParcelasLocal(parcelaUnica);
            onChange(parcelaUnica);
        }
    }, [gerarParcelas.data_inicial, parcelado, valorTotal, parcelasLocal.length]);

    useEffect(() => {
        if (parcelado && Number(gerarParcelas.quantidade) > 1) {
            const quantidade = Number(gerarParcelas.quantidade);
            const intervalo = Number(gerarParcelas.intervalo);

            let data_inicial: Moment;

            if (habilitarSelecaoPrimeiraParcela && tipoPrimeiraParcela === 'dias_corridos') {
                const diasCorridos = Number(gerarParcelas.dias_corridos_primeira_parcela);
                data_inicial = moment().add(diasCorridos, 'days');
            } else {
                data_inicial = gerarParcelas.data_inicial || moment();
            }

            const valores = calcularValoresParcelas(valorTotal, quantidade, valorPrimeiraParcelaCustomizado || undefined);

            const novasParcelas: TParcela[] = Array.from({ length: quantidade }, (_, index) => {
                const numero_parcela = index + 1;
                const data_vencimento = moment(data_inicial).add(intervalo * index, 'days');

                let valor_parcela: string;
                if (numero_parcela === 1) {
                    valor_parcela = valores.valorPrimeiraParcelaFormatado;
                } else if (numero_parcela === quantidade) {
                    valor_parcela = valores.valorUltimaParcelaFormatado;
                } else {
                    valor_parcela = valores.valorParcelaFormatado;
                }

                return {
                    numero_parcela,
                    data_vencimento,
                    valor_parcela
                };
            });

            const datasUnicas = novasParcelas.map(p => p.data_vencimento.format("YYYY-MM-DD"));
            if (new Set(datasUnicas).size === datasUnicas.length) {
                setParcelasLocal(novasParcelas);
                onChange(novasParcelas);
            }
        }
    }, [parcelado, gerarParcelas.quantidade, gerarParcelas.intervalo, gerarParcelas.data_inicial, gerarParcelas.dias_corridos_primeira_parcela, valorTotal, habilitarSelecaoPrimeiraParcela, tipoPrimeiraParcela, valorPrimeiraParcelaCustomizado]);


    function handleChangeGerarParcelaInput(key: keyof TObjParcelas, value: string | Moment | null) {
        if (key === "quantidade") {
            if (parcelado && value === '1') {
                setParcelado(false);
                setGerarParcelas(prev => ({ ...prev, quantidade: '1' }));
                setValorPrimeiraParcelaCustomizado(null);
                setValorPrimeiraParcelaInput('');
                const novaParcela = [{
                    numero_parcela: 1,
                    valor_parcela: valorTotal.toFixed(2).replace('.', ','),
                    data_vencimento: gerarParcelas.data_inicial || moment(),
                }];
                setParcelasLocal(novaParcela);
                onChange(novaParcela);
                return;
            }
            if (!parcelado && value !== '1') {
                setParcelado(true);
            }
        }

        if (key === "quantidade" || key === "intervalo" || key === "dias_corridos_primeira_parcela") {
            debouncedUpdateString(key, value as string);
        } else if (key === "data_inicial") {
            debouncedUpdateMoment(key, value as Moment | null);
        } else {
            setGerarParcelas(prev => ({ ...prev, [key]: value }));
        }
    }

    const handleLocalQuantidadeChange = (value: string) => {
        setLocalQuantidade(value);
        handleChangeGerarParcelaInput('quantidade', value);
    };

    const handleLocalIntervaloChange = (value: string) => {
        const valorNumerico = parseInt(value, 10);
        const valorValidado = isNaN(valorNumerico) || valorNumerico < 1 ? '1' : value;
        setLocalIntervalo(valorValidado);
        handleChangeGerarParcelaInput('intervalo', valorValidado);
    };
    const handleValorPrimeiraParcelaChange = (value: string) => {
        const apenasNumeros = value.replace(/\D/g, '');

        if (apenasNumeros === '') {
            setValorPrimeiraParcelaInput('');
            setValorPrimeiraParcelaCustomizado(null);
            setErroValorPrimeiraParcela('');
            onValorPrimeiraParcelaChange?.(null);
            return;
        }

        const valorEmCentavos = parseInt(apenasNumeros, 10);
        const valorNumerico = valorEmCentavos / 100;

        const valorFormatado = valorNumerico.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        setValorPrimeiraParcelaInput(valorFormatado);

        if (valorNumerico > valorTotal) {
            setErroValorPrimeiraParcela(`Valor não pode ser maior que ${formatarMoeda(valorTotal)}`);
            setValorPrimeiraParcelaCustomizado(null);
            onValorPrimeiraParcelaChange?.(null);
        } else {
            setValorPrimeiraParcelaCustomizado(valorNumerico);
            setErroValorPrimeiraParcela('');
            onValorPrimeiraParcelaChange?.(valorNumerico);
        }
    };


    function formatarMoeda(valor: string | number) {
        return formatarValorMonetario(valor);
    }

    return (
        <Box sx={{ width: "100%", maxWidth: 700, margin: "0 auto" }}>
            {habilitarSelecaoPrimeiraParcela && (
                <Grid container spacing={2} mb={2}>
                    <Grid size={{ xs: 12 }}>
                        <Typography sx={{ color: "primary.main", fontWeight: 500, mb: 1 }}>Primeira parcela:</Typography>
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                            <FormControlLabel
                                control={
                                    <Radio
                                        checked={tipoPrimeiraParcela === 'personalizada'}
                                        onChange={() => setTipoPrimeiraParcela('personalizada')}
                                        value="personalizada"
                                    />
                                }
                                label="Personalizada"
                            />
                            <FormControlLabel
                                control={
                                    <Radio
                                        checked={tipoPrimeiraParcela === 'dias_corridos'}
                                        onChange={() => setTipoPrimeiraParcela('dias_corridos')}
                                        value="dias_corridos"
                                    />
                                }
                                label="Dias corridos"
                            />
                        </Box>
                    </Grid>
                </Grid>
            )}
            <Grid container spacing={2} alignItems="flex-end" mb={parcelado ? 3 : 1}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        label="Valor *"
                        value={formatarMoeda(valorTotal)}
                        fullWidth
                        size="medium"
                        slotProps={{
                            htmlInput: { style: { fontWeight: 500 } },
                            input: { readOnly: true }
                        }}
                    />
                </Grid>
                {habilitarSelecaoPrimeiraParcela && tipoPrimeiraParcela === 'personalizada' && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <BasicDatePicker
                            label="Data primeira parcela"
                            required
                            blockPastDates
                            pastDates={moment()}
                            value={gerarParcelas.data_inicial ? gerarParcelas.data_inicial : moment()}
                            onChange={e => handleChangeGerarParcelaInput('data_inicial', moment(e, "YYYY-MM-DD"))}
                            formatString="DD/MM/YYYY"
                        />
                    </Grid>
                )}
                {habilitarSelecaoPrimeiraParcela && tipoPrimeiraParcela === 'dias_corridos' && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            label="Dias corridos"
                            type="number"
                            value={localDiasCorridos}
                            fullWidth
                            onChange={e => {
                                const valor = e.target.value;
                                setLocalDiasCorridos(valor);
                                setGerarParcelas(prev => ({ ...prev, dias_corridos_primeira_parcela: valor }));
                                const quantidadeDias = Number(valor);
                                const novaDataInicial = moment().add(quantidadeDias, 'days');
                                setGerarParcelas(prev => ({ ...prev, data_inicial: novaDataInicial }));
                            }}
                            size="medium"
                            slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { min: 1 }
                            }}
                        />
                    </Grid>
                )}
                {!habilitarSelecaoPrimeiraParcela && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <BasicDatePicker
                            label="Data primeira parcela"
                            required
                            blockPastDates
                            pastDates={moment()}
                            value={gerarParcelas.data_inicial ? gerarParcelas.data_inicial : moment()}
                            onChange={e => handleChangeGerarParcelaInput('data_inicial', moment(e, "YYYY-MM-DD"))}
                            formatString="DD/MM/YYYY"
                        />
                    </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                    <Typography sx={{ color: "success.main", fontWeight: 500, mb: 1 }}>Parcelar valor?</Typography>
                    <Box display="flex" alignItems="center">
                        <FormControlLabel
                            control={
                                <Radio
                                    checked={!parcelado}
                                    onChange={() => {
                                        setParcelado(false);
                                        setParcelasLocal([]);
                                        setValorPrimeiraParcelaCustomizado(null);
                                        setValorPrimeiraParcelaInput('');
                                        const parcelaUnica = [{
                                            numero_parcela: 1,
                                            valor_parcela: valorTotal.toFixed(2).replace('.', ','),
                                            data_vencimento: gerarParcelas.data_inicial || moment(),
                                        }];
                                        onChange(parcelaUnica);
                                    }}
                                    value="nao"
                                />
                            }
                            label="Não"
                        />
                        <FormControlLabel
                            control={
                                <Radio
                                    checked={parcelado}
                                    onChange={() => {
                                        setParcelado(true);
                                        if (parcelasLocal.length === 0) {
                                            const parcelasPadrao = [{
                                                numero_parcela: 1,
                                                valor_parcela: valorTotal.toFixed(2).replace('.', ','),
                                                data_vencimento: gerarParcelas.data_inicial || moment(),
                                            }];
                                            setParcelasLocal(parcelasPadrao);
                                            onChange(parcelasPadrao);
                                        }
                                    }}
                                    value="sim"
                                    sx={{ color: "success.main" }}
                                />
                            }
                            label="Sim"
                            sx={{ color: "success.main" }}
                        />
                    </Box>
                </Grid>
                {
                    parcelado && (
                        <>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Valor da primeira parcela"
                                    value={valorPrimeiraParcelaInput}
                                    fullWidth
                                    onChange={e => handleValorPrimeiraParcelaChange(e.target.value)}
                                    placeholder={`Máximo: ${formatarMoeda(valorTotal)}`}
                                    size="medium"
                                    error={!!erroValorPrimeiraParcela}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                    helperText={erroValorPrimeiraParcela}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Quantidade"
                                    type="number"
                                    value={localQuantidade}
                                    fullWidth
                                    onChange={e => handleLocalQuantidadeChange(e.target.value)}
                                    size="medium"
                                    disabled={false}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { min: 2, max: maxParcelas }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Intervalo entre parcelas"
                                    type="number"
                                    value={localIntervalo}
                                    fullWidth
                                    onChange={e => handleLocalIntervaloChange(e.target.value)}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { min: 1 }
                                    }}
                                    size="medium"
                                    disabled={false}
                                />
                            </Grid>
                        </>
                    )}
            </Grid>
            {
                parcelado && parcelasLocal.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}>
                            Parcelas Geradas ({parcelasLocal.length}):
                        </Typography>
                        <Box sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: '#fafafa',
                            maxHeight: 300,
                            overflowY: 'auto'
                        }}>
                            {parcelasLocal.map((parcela, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 1,
                                        px: 2,
                                        backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5',
                                        borderRadius: 1,
                                        mb: index < parcelasLocal.length - 1 ? 1 : 0
                                    }}
                                >
                                    <Typography fontWeight={600} variant="subtitle2">
                                        {parcela.numero_parcela}ª Parcela
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                        {habilitarSelecaoPrimeiraParcela && tipoPrimeiraParcela === 'dias_corridos' ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {parcela.numero_parcela === 1
                                                    ? `${gerarParcelas.dias_corridos_primeira_parcela} dias`
                                                    : `${Number(gerarParcelas.dias_corridos_primeira_parcela) + (Number(gerarParcelas.intervalo) * (parcela.numero_parcela - 1))} dias`
                                                }
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {parcela.data_vencimento.format('DD/MM/YYYY')}
                                            </Typography>
                                        )}
                                        <Typography fontWeight={600} variant="subtitle2" color="success.main">
                                            R$ {parcela.valor_parcela}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )
            }
            {
                erro &&
                <Box mt={1}>
                    <Alert severity="warning">{erro}</Alert>
                </Box>
            }
        </Box >
    );
}

export default PagamentoParcelado;